import React, { useState, useEffect } from 'react';
import { uploadSoilImage, analyzeSoilImage, directOpenAISoilAnalysis } from '../../services/api';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    CircularProgress,
    Alert,
    Paper,
    Divider,
    Chip
} from '@mui/material';

interface SoilAnalysisResult {
    _id: string;
    imageUrl: string;
    isSoil: boolean;
    nutrientLevels?: string;
    estimatedFertility?: string;
    cultivationLifespan?: string;
    organicMatter?: string;
    soilType?: string;
    pH?: number;
    recommendedCrops?: string[];
    fertilityYears?: number;
    analysisResults?: {
        nitrogen: string;
        phosphorus: string;
        potassium: string;
        moisture: string;
    };
    createdAt: string;
    analysisMethod?: string;
    directOpenAIResponse?: string;
}

const SoilMonitoringSimple: React.FC = () => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [result, setResult] = useState<SoilAnalysisResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [errorDetails, setErrorDetails] = useState<string | null>(null);
    const [isUsingFallback, setIsUsingFallback] = useState(false);
    const [uploadedImageId, setUploadedImageId] = useState<string | null>(null);
    const [uploadingState, setUploadingState] = useState<'idle' | 'uploading' | 'uploaded' | 'analyzing'>('idle');

    // Set OpenAI API key in localStorage for testing
    useEffect(() => {
        // This ensures the API key is available for the direct OpenAI fallback
        const openaiKey = process.env.REACT_APP_OPENAI_API_KEY;
        if (openaiKey && !localStorage.getItem('openai_api_key')) {
            console.log('Setting OpenAI API key in localStorage from environment');
            localStorage.setItem('openai_api_key', openaiKey);
        } else if (!localStorage.getItem('openai_api_key')) {
            // Hardcoded key as a last resort (for local development only)
            const hardcodedKey = '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';
            console.log('Setting hardcoded OpenAI API key in localStorage');
            localStorage.setItem('openai_api_key', hardcodedKey);
        }
    }, []);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            console.log('File selected:', file.name, file.type, `${file.size} bytes`);

            // Reset previous results and errors
            setResult(null);
            setError(null);
            setErrorDetails(null);
            setIsUsingFallback(false);
            setSelectedFile(file);
            setUploadedImageId(null);
            setUploadingState('idle');

            // Create a preview URL for the image
            const fileReader = new FileReader();
            fileReader.onload = () => {
                setPreviewUrl(fileReader.result as string);
                console.log('Preview URL created');
            };
            fileReader.readAsDataURL(file);

            // Validate file type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
                return;
            }

            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                setError('File size should be less than 10MB');
                return;
            }
        }
    };

    const handleUpload = async (event: React.FormEvent) => {
        event.preventDefault();
        console.log('Upload button clicked');

        if (!selectedFile) {
            console.log('No file selected');
            setError('Please select an image to upload');
            return;
        }

        try {
            console.log('Starting upload process');
            setLoading(true);
            setError(null);
            setErrorDetails(null);
            setIsUsingFallback(false);
            setUploadingState('uploading');

            // Display debugging info in console
            console.log('Selected file:', selectedFile.name, selectedFile.type, `${Math.round(selectedFile.size / 1024)} KB`);
            console.log('Environment:', process.env.NODE_ENV);
            console.log('API URL:', process.env.REACT_APP_API_URL);

            console.log('Uploading file:', selectedFile.name);
            try {
                // First step: Just upload the image without analysis
                const formData = new FormData();
                formData.append('image', selectedFile);

                const response = await uploadSoilImage(selectedFile);
                console.log('Upload successful:', response);
                setUploadedImageId(response._id);
                setUploadingState('uploaded');
            } catch (backendError: any) {
                console.error('Backend error during soil image upload:', backendError);
                setError('Failed to upload soil image. Please try again.');
                setUploadingState('idle');
            }

            setLoading(false);
        } catch (error: any) {
            console.error('Error during soil image upload:', error);
            setError('Failed to upload soil image: ' + (error.message || 'Unknown error'));
            setErrorDetails(error.stack || null);
            setLoading(false);
            setUploadingState('idle');
        }
    };

    const handleAnalyze = async () => {
        if (!uploadedImageId) {
            setError('No uploaded image to analyze');
            return;
        }

        try {
            console.log('Starting analysis process');
            setLoading(true);
            setError(null);
            setErrorDetails(null);
            setUploadingState('analyzing');

            try {
                // Second step: Analyze the previously uploaded image
                const response = await analyzeSoilImage(uploadedImageId);
                console.log('Analysis successful:', response);
                setResult(response);
            } catch (backendError: any) {
                console.error('Backend error during soil analysis:', backendError);

                // Check if it's a CORS or network error
                if (backendError.code === 'ERR_NETWORK' ||
                    (backendError.message && (
                        backendError.message.includes('Network Error') ||
                        backendError.message.includes('CORS')
                    ))) {
                    console.log('CORS or network error detected. Using direct OpenAI fallback...');
                    setIsUsingFallback(true);

                    // Use direct OpenAI fallback
                    const fallbackResult = await directOpenAISoilAnalysis(selectedFile!);
                    console.log('Received fallback result:', fallbackResult);
                    setResult(fallbackResult);
                } else {
                    // Re-throw other errors
                    throw backendError;
                }
            }

            setLoading(false);
            setUploadingState('idle');
        } catch (error: any) {
            console.error('Error during soil analysis:', error);
            setError('Failed to analyze soil image: ' + (error.message || 'Unknown error'));
            setErrorDetails(error.stack || null);
            setLoading(false);
            setUploadingState('idle');
        }
    };

    // Direct OpenAI fallback handler
    const handleDirectOpenAI = async () => {
        if (!selectedFile) {
            setError('Please select an image to upload');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setErrorDetails(null);
            setIsUsingFallback(true);

            console.log('Using direct EcoFusion AI analysis...');
            const res = await directOpenAISoilAnalysis(selectedFile);
            console.log('Received direct EcoFusion AI result:', res);

            setResult(res);
            setLoading(false);
        } catch (error: any) {
            console.error('Error during direct EcoFusion AI analysis:', error);
            setError('Failed to analyze with EcoFusion AI: ' + (error.message || 'Unknown error'));
            setLoading(false);
        }
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    AI Soil Monitoring
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                    Upload a soil image for detailed AI analysis of soil fertility and properties. Our AI will detect if the image contains soil and analyze its fertility characteristics.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                    {/* Left Column - Upload Section */}
                    <Box sx={{ flex: 1 }}>
                        <Box
                            component="form"
                            onSubmit={handleUpload}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2,
                                mb: 2,
                            }}
                        >
                            {/* Buttons in a single row */}
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
                                <Button
                                    variant="contained"
                                    component="label"
                                    sx={{
                                        minHeight: 48,
                                        borderRadius: 8,
                                        fontWeight: '600',
                                        textTransform: 'none',
                                        boxShadow: '0px 4px 10px rgba(0,0,0,0.12)',
                                        flex: 1
                                    }}
                                >
                                    Select Soil Image
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                </Button>

                                {/* Upload button - First step */}
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={!selectedFile || loading || uploadingState === 'uploaded' || uploadingState === 'analyzing'}
                                    sx={{
                                        minHeight: 48,
                                        borderRadius: 8,
                                        fontWeight: '600',
                                        textTransform: 'none',
                                        boxShadow: '0px 4px 10px rgba(0,0,0,0.12)',
                                        flex: 1
                                    }}
                                >
                                    {loading && uploadingState === 'uploading' ? <CircularProgress size={24} color="inherit" /> : 'Upload Soil Image'}
                                </Button>
                            </Box>

                            {/* Analyze button - Second step */}
                            {uploadedImageId && (
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleAnalyze}
                                    disabled={loading || uploadingState === 'analyzing'}
                                    sx={{
                                        width: '100%',
                                        minHeight: 48,
                                        borderRadius: 8,
                                        fontWeight: '600',
                                        textTransform: 'none',
                                        mt: 2,
                                        bgcolor: '#4caf50', // Green color for analyze button
                                        '&:hover': {
                                            bgcolor: '#388e3c' // Darker green on hover
                                        }
                                    }}
                                >
                                    {loading && uploadingState === 'analyzing' ? <CircularProgress size={24} color="inherit" /> : 'Analyze Soil'}
                                </Button>
                            )}

                            {/* Secondary button for direct OpenAI analysis */}
                            {error && (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleDirectOpenAI}
                                    disabled={!selectedFile || loading}
                                    sx={{
                                        width: '100%',
                                        minHeight: 48,
                                        borderRadius: 8,
                                        fontWeight: '600',
                                        textTransform: 'none',
                                        mt: 1
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Try Direct EcoFusion AI Analysis'}
                                </Button>
                            )}

                            {/* Error message - neutral color instead of red */}
                            {error && (
                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <Alert
                                        severity="info"
                                        sx={{
                                            padding: 2,
                                            fontSize: '0.95rem',
                                            fontWeight: 500,
                                            backgroundColor: '#e8eaf6', // Light blue/indigo color
                                            color: '#3f51b5'
                                        }}
                                    >
                                        <Typography fontWeight="bold">{error}</Typography>

                                        <Typography sx={{ mt: 1, fontSize: '0.85rem' }}>
                                            Troubleshooting:
                                        </Typography>
                                        <ol style={{ margin: '0.5rem 0 0 1rem', fontSize: '0.85rem' }}>
                                            <li>Check browser console for detailed errors (F12)</li>
                                            <li>Make sure the backend server is running</li>
                                            <li>Check if CORS is properly configured</li>
                                            <li>Verify your image is under 10MB and in a supported format</li>
                                        </ol>
                                    </Alert>
                                </Box>
                            )}

                            {errorDetails && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    {errorDetails}
                                </Alert>
                            )}

                            {previewUrl && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <img
                                        src={previewUrl}
                                        alt="Soil sample preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '200px',
                                            borderRadius: '8px',
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Right Column - Results Section */}
                    <Box sx={{ flex: 1 }}>
                        {result && (
                            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                                {!result.isSoil ? (
                                    <Alert severity="warning">
                                        No soil detected in the image! Please upload a clear image of soil.
                                    </Alert>
                                ) : (
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="h6">
                                                Analysis Results
                                            </Typography>
                                            {result.analysisMethod && (
                                                <Chip
                                                    size="small"
                                                    color={result.analysisMethod.includes('Direct') ? 'secondary' : 'primary'}
                                                    label={result.analysisMethod.replace('OpenAI', 'EcoFusion AI')}
                                                />
                                            )}
                                        </Box>

                                        {/* Show direct OpenAI response if available */}
                                        {result.directOpenAIResponse && (
                                            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                                                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                                    EcoFusion AI Direct Analysis:
                                                </Typography>
                                                <Typography variant="body2">
                                                    {result.directOpenAIResponse}
                                                </Typography>
                                            </Box>
                                        )}

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                            <Box sx={{ width: '50%', mb: 2 }}>
                                                <Typography variant="subtitle2">Soil Type:</Typography>
                                                <Typography variant="body1">{result.soilType}</Typography>
                                            </Box>
                                            <Box sx={{ width: '50%', mb: 2 }}>
                                                <Typography variant="subtitle2">Fertility:</Typography>
                                                <Typography variant="body1">{result.estimatedFertility}</Typography>
                                            </Box>
                                            <Box sx={{ width: '50%', mb: 2 }}>
                                                <Typography variant="subtitle2">Fertility Duration:</Typography>
                                                <Typography variant="body1">
                                                    {result.fertilityYears} years
                                                </Typography>
                                            </Box>
                                            <Box sx={{ width: '50%', mb: 2 }}>
                                                <Typography variant="subtitle2">pH Level:</Typography>
                                                <Typography variant="body1">{result.pH}</Typography>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Typography variant="subtitle1" gutterBottom>
                                            Detailed Properties
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                            <Box sx={{ width: '50%', mb: 1 }}>
                                                <Typography variant="body2">
                                                    Organic Matter: {result.organicMatter}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ width: '50%', mb: 1 }}>
                                                <Typography variant="body2">
                                                    Nutrient Levels: {result.nutrientLevels}
                                                </Typography>
                                            </Box>

                                            {result.analysisResults && (
                                                <>
                                                    <Box sx={{ width: '50%', mb: 1 }}>
                                                        <Typography variant="body2">
                                                            Nitrogen: {result.analysisResults.nitrogen}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ width: '50%', mb: 1 }}>
                                                        <Typography variant="body2">
                                                            Phosphorus: {result.analysisResults.phosphorus}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ width: '50%', mb: 1 }}>
                                                        <Typography variant="body2">
                                                            Potassium: {result.analysisResults.potassium}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ width: '50%', mb: 1 }}>
                                                        <Typography variant="body2">
                                                            Moisture: {result.analysisResults.moisture}
                                                        </Typography>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>

                                        <Divider sx={{ my: 2 }} />

                                        <Typography variant="subtitle1" gutterBottom>
                                            Recommended Crops
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {result.recommendedCrops?.map((crop, index) => (
                                                <Chip
                                                    key={index}
                                                    label={crop}
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </Paper>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

// Replacing the export statement
export default SoilMonitoringSimple;
